import { getStateShorthand } from '@/core/helpers/utils';
import { capitalize } from '@/core/helpers/utils.js';
import {
    EVENTS,
    TrackingService,
} from '@/core/services/TrackingService/TrackingService.js';
import { LEAGUES_CONFIG } from '@/core/static-json/competitionLeaguesConfig.js';
import { COMPETITION_CONSTANTS } from '@/core/static-json/competitionsConfig.js';
import SecureApi from '@/flows/Authentication/services/SecureApi.js';
import _ from 'lodash';
import moment from 'moment';

function reportError(message, error, callback) {
    console.error(message, error);
    if (typeof callback === 'function') callback();
}

function formatScore(score) {
    return !score || score === 0 ? 0 : +score.toFixed(2);
}

export function formatScoreRounded(score) {
    return !score || score === 0 ? 0 : +Math.round(score);
}

export function formatScoreDisplay(score) {
    if (!score || score === 0) return '0';
    return new Intl.NumberFormat('en-US').format(+score.toFixed(2));
}

export function formatScoreRoundedDisplay(score) {
    if (!score || score === 0) return '0';
    return new Intl.NumberFormat('en-US').format(Math.round(score));
}

function formatDateRange(startAt, endAt) {
    const startMonth = moment(startAt).format('MMMM');
    const startDay = moment(startAt).format('D');
    const endMonth = moment(endAt).format('MMMM');
    const endDay = moment(endAt).format('D');

    if (startMonth === endMonth) {
        return `${startMonth} ${startDay} - ${endDay}`;
    } else {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }
}

function getAndFormatWinnersDates(eventEndAt) {
    const winnersStart = moment(eventEndAt).add(1, 'day');
    const winnersEnd = moment(winnersStart).add(8, 'days');
    return formatDateRange(winnersStart, winnersEnd);
}

export function getGradeEnding(grade) {
    switch (grade.toString()) {
        case '1':
            return 'st';
        case '2':
            return 'nd';
        case '3':
            return 'rd';
        case 'K':
            return '';
        default:
            return 'th';
    }
}

export default {
    namespaced: true,
    state: {
        competitionsConfig: [],
        overviewTopStateSchools: [],
        neighbors: [],
        overviewMyClasses: [],
        overviewTopClasses: [],
        overviewRisingClasses: [],
        selectedYearlyLeague: LEAGUES_CONFIG[LEAGUES_CONFIG.length - 1],
        seasonTotalParticipants: 0,
        teams: [],
        teamsByCompetition: {},
        leaderboard: [],
        infiniteLeaderboard: [],
        competitionClassCode: undefined,
        competitionTeam: null,
        customGrade: null,
        selectedCompetition: null,
        leaderboardTopStudents: [],
        leaderboardMostImproved: [],
        leaderboardTopHomePlayers: [],
        leaderboardSuggestions: [],
        leaderboardUpdateTime: undefined,
        ownRecords: [],
        teamsIsLoaded: false,
        pinnedClasses: [],
        suggestionsIsLoading: false,
        suggestionsOptionsAvailable: 0,
        suggestionsOptionsLoaded: 0,
    },
    getters: {
        pinnedClassCodes: (state) =>
            state.pinnedClasses.map((item) => item.classCode),
        competitionsConfig: (state) => {
            const now = moment();
            return state.competitionsConfig
                .map((competition) => {
                    let status;
                    if (
                        now.isBetween(
                            moment.utc(competition.startAt),
                            moment.utc(competition.endAt),
                        )
                    ) {
                        status = COMPETITION_CONSTANTS.LIVE;
                    }
                    if (now.isAfter(moment.utc(competition.endAt))) {
                        status = COMPETITION_CONSTANTS.ENDED;
                    }
                    if (now.isBefore(moment.utc(competition.startAt))) {
                        status = COMPETITION_CONSTANTS.NEW;
                    }
                    return {
                        ...competition,
                        status,
                    };
                })
                .filter((competition) => competition.id !== 'freeTrial');
        },
        currentCompetitionEvent: (state, getters) => {
            const fakeNow = sessionStorage.getItem('fakeNow');
            const now = fakeNow ? moment(fakeNow, 'DD.MM.YYYY') : moment();
            const config = getters.competitionsConfig;
            const inProgressEvent = config.find(
                (competition) =>
                    moment(competition.startAt).isBefore(now) &&
                    moment(competition.endAt).isAfter(now),
            );
            function makePrev(configItem) {
                const prevConfig = configItem;
                return prevConfig
                    ? {
                            status: 'Past',
                            label: 'Past event:',
                            event: prevConfig,
                            dates: formatDateRange(
                                prevConfig.startAt,
                                prevConfig.endAt,
                            ),
                            winners: getAndFormatWinnersDates(prevConfig.endAt),
                        }
                    : undefined;
            }
            if (inProgressEvent) {
                return {
                    status: 'Current',
                    label: 'Current event:',
                    dates: formatDateRange(
                        inProgressEvent.startAt,
                        inProgressEvent.endAt,
                    ),
                    winners: getAndFormatWinnersDates(inProgressEvent.endAt),
                    event: inProgressEvent,
                };
            }
            const nextEvent = config.find((competition) =>
                moment(competition.startAt).isAfter(now),
            );
            if (nextEvent) {
                const nextEventIndex = config.findIndex(
                    (item) => item.id === nextEvent.id,
                );

                const prevEvent = makePrev(config[nextEventIndex - 1]);

                return {
                    status: 'Next',
                    label: 'Next event:',
                    dates: formatDateRange(nextEvent.startAt, nextEvent.endAt),
                    winners: getAndFormatWinnersDates(nextEvent.endAt),
                    event: nextEvent,
                    prevEvent,
                };
            }
            const prevEvent = makePrev(config[config.length - 1]);
            return {
                status: 'Ended',
                label: 'Season has ended',
                dates: '',
                prevEvent,
            };
        },
        currentCompetitionBanner: (state, getters) => {
            const fakeNow = sessionStorage.getItem('fakeNow');
            const now = fakeNow ? moment(fakeNow, 'DD.MM.YYYY') : moment();
            const config = getters.competitionsConfig;
            const inProgressEvent = config.find(
                (competition) =>
                    moment(competition.startAt).isBefore(now) &&
                    moment(competition.endAt).isAfter(now),
            );
            if (inProgressEvent) {
                return {
                    status: 'Current',
                    event: inProgressEvent,
                };
            }
            const previousEvent = config.find((competition) => {
                const endDate = moment(competition.endAt);
                const twoWorkingDaysAfterEnd = moment(
                    competition.endAt,
                ).clone();
                // Add working days (skip weekends)
                let daysAdded = 0;
                while (daysAdded < 2) {
                    twoWorkingDaysAfterEnd.add(1, 'days');
                    // Skip weekends (0 is Sunday, 6 is Saturday in moment.js)
                    if (
                        twoWorkingDaysAfterEnd.day() !== 0 &&
                        twoWorkingDaysAfterEnd.day() !== 6
                    ) {
                        daysAdded++;
                    }
                }
                twoWorkingDaysAfterEnd.endOf('day'); // Round up to end of day (23:59:59)
                return (
                    endDate.isBefore(now) &&
                    now.isBefore(twoWorkingDaysAfterEnd)
                );
            });
            if (previousEvent) {
                return {
                    status: 'Ended',
                    event: previousEvent,
                };
            }
            const nextEvent = config.find((competition) =>
                moment(competition.startAt).isAfter(now),
            );
            if (nextEvent) {
                return {
                    status: 'Next',
                    label: 'Next event:',
                    dates: formatDateRange(nextEvent.startAt, nextEvent.endAt),
                    winners: getAndFormatWinnersDates(nextEvent.endAt),
                    event: nextEvent,
                };
            }
            return null;
        },
        /** @deprecated use state */
        teams: (state) => state.teams,
        getCompetitionGrade: (state) => {
            return state.customGrade || state.competitionTeam?.grade;
        },
        getCompetitionGradeLabel: (state, getters) => {
            const grade = getters.getCompetitionGrade;
            if (grade) {
                const ending = getGradeEnding(grade);
                return `${grade}${ending} Grade`;
            } else {
                return '';
            }
        },
        /**
         * @deprecated state.leaderboard is not a main leaderboard
         */
        leaderboardFilteredCount: (state) => {
            return state.leaderboard[0]?.filteredParticipants || 0;
        },
        /**
         * @deprecated state.leaderboard is not a main leaderboard
         */
        leaderboardTotalCount: (state) => {
            return state.leaderboard[0]?.totalParticipants || 0;
        },
        leaderboardUpdateText: (state) => () => {
            const time = moment
                .duration(moment().diff(state.leaderboardUpdateTime))
                .humanize();
            return `Last updated ${time} ago`;
        },
        leaguePositionRanges: (state) => {
            const totalParticipants = state.seasonTotalParticipants;
            let positionRanges = {};

            LEAGUES_CONFIG.forEach((league) => {
                const fromPos = Math.floor(
                    totalParticipants * ((100 - league.to) / 100),
                );
                positionRanges[league.id] = {
                    from: fromPos === 0 ? 0 : fromPos + 1,
                    to: Math.floor(
                        totalParticipants * ((100 - league.from) / 100),
                    ),
                };
            });

            return positionRanges;
        },
        currentLeagueRange: (state, getters) => {
            return getters.leaguePositionRanges[state.selectedYearlyLeague.id];
        },
        /* Ideally must be only one, but we have more */
        competitionSchools: (state) => {
            return _.chain(state.teams)
                .groupBy((team) => `${team.school}|${team.state}`)
                .map((teams) => ({
                    school: teams[0].school,
                    state: teams[0].state,
                    grades: _.uniq(teams.map((t) => t.grade)),
                }))
                .value();
        },
        suggestionsLoadingProgress: (state) => {
            return Math.floor(
                (100 * state.suggestionsOptionsLoaded) /
                state.suggestionsOptionsAvailable,
            );
        },
    },
    mutations: {
        setPinnedClasses: (state, pinnedClasses) => {
            state.pinnedClasses = pinnedClasses.map((item) => {
                return {
                    ...item,
                    totalScore: formatScore(item.totalScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    // Country-specific formatted versions
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                };
            });
        },
        addPinnedClass: (state, classObject) => {
            state.pinnedClasses.push({
                ...classObject,
                totalScore: formatScore(classObject.totalScore),
                totalScoreRounded: formatScoreRounded(classObject.totalScore),
                // Country-specific formatted versions
                totalScoreFormatted: formatScoreDisplay(classObject.totalScore),
                totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                    classObject.totalScore,
                ),
            });
        },
        removePinnedClass: (state, classCode) => {
            _.remove(
                state.pinnedClasses,
                (item) => item.classCode === classCode,
            );
        },
        setCompetitionsConfig: (state, config) => {
            state.competitionsConfig = config;
            state.selectedCompetition = config[0];
        },
        addOrUpdateTeam: (state, team) => {
            const pos = state.teams.findIndex(
                (t) => t.classCode === team.classCode,
            );
            if (pos > -1) state.teams[pos] = team;
            else state.teams.push(team);
        },
        setSelectedYearlyLeague: (state, payload) => {
            state.selectedYearlyLeague = payload;
        },
        setTeams: (state, teams) => {
            // console.debug(
            //     'setTeams mutation',
            //     teams,
            //     state.competitionClassCode,
            // );
            state.teams = teams;
            if (state.competitionClassCode) {
                state.competitionTeam = state.teams.find(
                    (team) => team.classCode === state.competitionClassCode,
                );
            }
        },
        setTeamsByCompetition: (state, teams) => {
            console.debug('setTeamsByCompetition mutation', teams);
            state.teamsByCompetition = teams;
        },
        setSeasonTotalParticipants: (state, totalParticipants) => {
            state.seasonTotalParticipants = totalParticipants;
        },
        setLeaderboard: (state, leaderboard) => {
            state.leaderboard = leaderboard.map((item) => {
                return {
                    ...item,
                    schoolScore: formatScore(item.schoolScore),
                    homeScore: formatScore(item.homeScore),
                    totalScore: formatScore(item.totalScore),
                    seasonScore: formatScore(item.seasonScore),
                    schoolScoreRounded: formatScoreRounded(item.schoolScore),
                    homeScoreRounded: formatScoreRounded(item.homeScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    seasonScoreRounded: formatScoreRounded(item.seasonScore),
                    // Country specific formatted versions
                    schoolScoreFormatted: formatScoreDisplay(item.schoolScore),
                    homeScoreFormatted: formatScoreDisplay(item.homeScore),
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    seasonScoreFormatted: formatScoreDisplay(item.seasonScore),
                    schoolScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.schoolScore,
                    ),
                    homeScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.homeScore,
                    ),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                    seasonScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.seasonScore,
                    ),
                };
            });
        },
        setNeighbors: (state, neighbors) => {
            state.neighbors = neighbors;
        },
        setOverviewMyClasses: (state, leaderboard) => {
            state.overviewMyClasses = leaderboard.map((item) => {
                return {
                    ...item,
                    totalScore: formatScore(item.totalScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    // Country specific formatted versions
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                };
            });
        },
        setOverviewTopClasses: (state, leaderboard) => {
            state.overviewTopClasses = leaderboard.map((item) => {
                return {
                    ...item,
                    state: getStateShorthand(item.state),
                    totalScore: formatScore(item.totalScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    // Country specific formatted versions
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                };
            });
        },
        setOverviewRisingClasses: (state, leaderboard) => {
            state.overviewRisingClasses = leaderboard.map((item) => {
                return {
                    ...item,
                    state: getStateShorthand(item.state),
                };
            });
        },
        setOverviewTopStateSchools: (state, leaderboard) => {
            state.overviewTopStateSchools = leaderboard.map((item) => {
                return {
                    ...item,
                    state: getStateShorthand(item.state),
                    totalScore: formatScore(item.totalScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    // Country specific formatted versions
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                };
            });
        },
        setInfiniteLeaderboard: (state, leaderboard) => {
            state.infiniteLeaderboard = leaderboard.map((item, index) => {
                return {
                    ...item,
                    key: item.position || 'empty' + index,
                    schoolScore: formatScore(item.schoolScore),
                    homeScore: formatScore(item.homeScore),
                    totalScore: formatScore(item.totalScore),
                    seasonScore: formatScore(item.seasonScore),
                    schoolScoreRounded: formatScoreRounded(item.schoolScore),
                    homeScoreRounded: formatScoreRounded(item.homeScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    seasonScoreRounded: formatScoreRounded(item.seasonScore),
                    // Country specific formatted versions
                    schoolScoreFormatted: formatScoreDisplay(item.schoolScore),
                    homeScoreFormatted: formatScoreDisplay(item.homeScore),
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    seasonScoreFormatted: formatScoreDisplay(item.seasonScore),
                    schoolScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.schoolScore,
                    ),
                    homeScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.homeScore,
                    ),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                    seasonScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.seasonScore,
                    ),
                    previousScoresFormatted: item.previousScores
                        ? Object.entries(item.previousScores).reduce(
                                (acc, [key, value]) => {
                                    acc[key] = formatScoreDisplay(value);
                                    return acc;
                                },
                                {},
                            )
                        : {},
                    previousScoresRoundedFormatted: item.previousScores
                        ? Object.entries(item.previousScores).reduce(
                                (acc, [key, value]) => {
                                    acc[key] = formatScoreRoundedDisplay(value);
                                    return acc;
                                },
                                {},
                            )
                        : {},
                };
            });
        },
        setOwnRecords: (state, leaderboard) => {
            state.ownRecords = leaderboard.map((item, index) => {
                return {
                    ...item,
                    key: item.position || 'empty' + index,
                    schoolScore: formatScore(item.schoolScore),
                    homeScore: formatScore(item.homeScore),
                    totalScore: formatScore(item.totalScore),
                    seasonScore: formatScore(item.seasonScore),
                    schoolScoreRounded: formatScoreRounded(item.schoolScore),
                    homeScoreRounded: formatScoreRounded(item.homeScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    seasonScoreRounded: formatScoreRounded(item.seasonScore),
                    // Country specific formatted versions
                    schoolScoreFormatted: formatScoreDisplay(item.schoolScore),
                    homeScoreFormatted: formatScoreDisplay(item.homeScore),
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    seasonScoreFormatted: formatScoreDisplay(item.seasonScore),
                    schoolScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.schoolScore,
                    ),
                    homeScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.homeScore,
                    ),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                    seasonScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.seasonScore,
                    ),
                    previousScoresFormatted: item.previousScores
                        ? Object.entries(item.previousScores).reduce(
                                (acc, [key, value]) => {
                                    acc[key] = formatScoreDisplay(value);
                                    return acc;
                                },
                                {},
                            )
                        : {},
                    previousScoresRoundedFormatted: item.previousScores
                        ? Object.entries(item.previousScores).reduce(
                                (acc, [key, value]) => {
                                    acc[key] = formatScoreRoundedDisplay(value);
                                    return acc;
                                },
                                {},
                            )
                        : {},
                };
            });
        },
        setSelectedCompetition: (state, payload) => {
            state.selectedCompetition = payload;
        },
        setCompetitionClassCode: (state, classCode) => {
            console.debug(
                'setCompetitionClassCode mutation',
                state.teams,
                classCode,
            );
            state.competitionClassCode = classCode;
            state.competitionTeam = state.teams.find(
                (team) => team.classCode === classCode,
            );
            if (classCode) {
                state.customGrade = null;
            }
        },
        setCustomGrade: (state, grade) => {
            state.customGrade = grade;
        },
        setLeaderboardTopStudents: (state, data) => {
            state.leaderboardTopStudents = data.map((item) => {
                return {
                    ...item,
                    homeScoreRounded: formatScoreRounded(item.homeScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    homeChangeRounded: formatScoreRounded(item.homeChange),
                    totalChangeRounded: formatScoreRounded(item.totalChange),
                    // Country specific formatted versions
                    homeScoreFormatted: formatScoreDisplay(item.homeScore),
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    homeChangeFormatted: formatScoreDisplay(item.homeChange),
                    totalChangeFormatted: formatScoreDisplay(item.totalChange),
                    homeScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.homeScore,
                    ),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                    homeChangeRoundedFormatted: formatScoreRoundedDisplay(
                        item.homeChange,
                    ),
                    totalChangeRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalChange,
                    ),
                };
            });
        },
        setLeaderboardMostImproved: (state, data) => {
            state.leaderboardMostImproved = data.map((item) => {
                return {
                    ...item,
                    homeScoreRounded: formatScoreRounded(item.homeScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    homeChangeRounded: formatScoreRounded(item.homeChange),
                    totalChangeRounded: formatScoreRounded(item.totalChange),
                    // Country specific formatted versions
                    homeScoreFormatted: formatScoreDisplay(item.homeScore),
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    homeChangeFormatted: formatScoreDisplay(item.homeChange),
                    totalChangeFormatted: formatScoreDisplay(item.totalChange),
                    homeScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.homeScore,
                    ),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                    homeChangeRoundedFormatted: formatScoreRoundedDisplay(
                        item.homeChange,
                    ),
                    totalChangeRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalChange,
                    ),
                };
            });
        },
        setLeaderboardTopHomePlayers: (state, data) => {
            state.leaderboardTopHomePlayers = data.map((item) => {
                return {
                    ...item,
                    homeScoreRounded: formatScoreRounded(item.homeScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    homeChangeRounded: formatScoreRounded(item.homeChange),
                    totalChangeRounded: formatScoreRounded(item.totalChange),
                    // Country specific formatted versions
                    homeScoreFormatted: formatScoreDisplay(item.homeScore),
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    homeChangeFormatted: formatScoreDisplay(item.homeChange),
                    totalChangeFormatted: formatScoreDisplay(item.totalChange),
                    homeScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.homeScore,
                    ),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                    homeChangeRoundedFormatted: formatScoreRoundedDisplay(
                        item.homeChange,
                    ),
                    totalChangeRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalChange,
                    ),
                };
            });
        },
        setSuggestionLeaderboard: (state, data) => {
            state.leaderboardSuggestions = data.map((item) => {
                return {
                    ...item,
                    totalScore: formatScore(item.totalScore),
                    totalScoreRounded: formatScoreRounded(item.totalScore),
                    // Country-specific formatted versions
                    totalScoreFormatted: formatScoreDisplay(item.totalScore),
                    totalScoreRoundedFormatted: formatScoreRoundedDisplay(
                        item.totalScore,
                    ),
                };
            });
        },
        setLeaderboardUpdateTime: (state, payload) => {
            state.leaderboardUpdateTime = payload;
        },
        setTeamsIsLoaded: (state, data) => {
            state.teamsIsLoaded = data;
        },
        setSuggestionsLoading(state, isLoading) {
            state.suggestionsIsLoading = isLoading;
        },
        setSuggestionsOptionsAvailable(state, count) {
            state.suggestionsOptionsAvailable = count;
        },
        setSuggestionsOptionsLoaded(state, count) {
            state.suggestionsOptionsLoaded = count;
        },
    },
    actions: {
        fetchCompetitionsConfig: async ({ commit }) => {
            try {
                const result = await SecureApi().get('competitions/config/all');
                if (!result.data.success) {
                    reportError('Error fetching competitions config', result);
                } else {
                    const data = result.data.data;
                    commit('setCompetitionsConfig', data);
                }
            } catch (error) {
                reportError('Error fetching competitions config', error);
            }
        },
        fetchTeams: async ({ commit, rootGetters, dispatch, getters }) => {
            const currentEvent = getters.currentCompetitionEvent;
            let queryParams = {};

            // a few weeks after the season give access to the last competition
            if (currentEvent.status === 'Ended') {
                const lastEvent = currentEvent.prevEvent.event;
                if (
                    lastEvent &&
                    moment().isBefore(moment(lastEvent.endAt).add(4, 'weeks'))
                ) {
                    queryParams.competitionId = lastEvent.id;
                }
            }

            try {
                const result = await SecureApi().get('competitions', {
                    params: queryParams,
                });
                if (!result.data.success) {
                    reportError(
                        'Error reading competition teams',
                        result,
                        () => {
                            commit('setTeams', []);
                        },
                    );
                }

                commit('setTeams', result.data.data);
                commit('setTeamsIsLoaded', true);
                if (
                    rootGetters.user &&
                    !rootGetters.user?.flags?.sentIsCompetitionUserMP
                ) {
                    new TrackingService().track(EVENTS.SET_FLAG, {
                        prop: 'Is Competition User',
                        flagValue: 'yes',
                    });
                    await dispatch('flipFlag', 'sentIsCompetitionUserMP', {
                        root: true,
                    });
                }
            } catch (error) {
                reportError('Error reading competition teams', error, () => {
                    commit('setTeams', []);
                    commit('setTeamsIsLoaded', true);
                });
            }
        },

        fetchTeamsByCompetition: async ({ commit }, payload) => {
            const competitionId = payload?.competitionId || null;
            let queryParams = {};
            if (competitionId) {
                queryParams.competitionId = competitionId;
            }
            try {
                const result = await SecureApi().get('competitions', {
                    params: queryParams,
                });
                if (!result.data.success) {
                    reportError(
                        'Error reading competition teams',
                        result,
                        () => {
                            commit('setTeams', []);
                        },
                    );
                }

                commit('setTeamsByCompetition', result.data.data);
                commit('setTeamsIsLoaded', true);
            } catch (error) {
                reportError('Error reading competition teams', error, () => {
                    commit('setTeamsByCompetition', []);
                    commit('setTeamsIsLoaded', true);
                });
            }
        },

        fetchOverviewRisingClasses: async ({ state, commit }) => {
            const competitionId = state.selectedCompetition?.id;
            try {
                const result = await SecureApi().get(
                    'competitions/leaderboard',
                    {
                        params: {
                            leaderboardId: `classRaise_${competitionId}`,
                            pageSize: 5,
                        },
                    },
                );
                if (result.data.success) {
                    commit('setOverviewRisingClasses', result.data.data);
                } else {
                    reportError(
                        'Error reading competition overview rising classes',
                        result,
                        () => {
                            commit('setOverviewRisingClasses', []);
                        },
                    );
                }
            } catch (error) {
                reportError(
                    'Error reading competition overview rising classes',
                    error,
                    () => {
                        commit('setOverviewRisingClasses', []);
                    },
                );
            }
        },
        fetchOverviewTopClasses: async ({ state, commit }, grade) => {
            const competitionId = state.selectedCompetition?.id;
            try {
                const result = await SecureApi().get(
                    'competitions/leaderboard',
                    {
                        params: {
                            leaderboardId: `${competitionId}`,
                            pageSize: 5,
                            grade,
                        },
                    },
                );
                if (result.data.success) {
                    commit('setOverviewTopClasses', result.data.data);
                } else {
                    reportError(
                        'Error reading competition overview top classes',
                        result,
                        () => {
                            commit('setOverviewTopClasses', []);
                        },
                    );
                }
            } catch (error) {
                reportError(
                    'Error reading competition overview top classes',
                    error,
                    () => {
                        commit('setOverviewTopClasses', []);
                    },
                );
            }
        },
        fetchOverviewMyClasses: async ({ state, commit }) => {
            const competitionId = state.selectedCompetition?.id;
            try {
                const result = await SecureApi().get('competitions', {
                    params: { competitionId },
                });
                if (result.data.success) {
                    commit('setOverviewMyClasses', result.data.data);
                } else {
                    reportError(
                        'Error reading competition overview my classes',
                        result,
                        () => {
                            commit('setOverviewMyClasses', []);
                        },
                    );
                }
            } catch (error) {
                reportError(
                    'Error reading competition overview my classes',
                    error,
                    () => {
                        commit('setOverviewMyClasses', []);
                    },
                );
            }
        },
        fetchOverviewTopStateSchools: async ({
            state,
            commit,
            rootGetters,
        }) => {
            const competitionId = state.selectedCompetition?.id;
            const participantState =
                state.teams?.[0]?.state || rootGetters.user?.country;

            try {
                const result = await SecureApi().get(
                    'competitions/leaderboard',
                    {
                        params: {
                            leaderboardId: `schools_${competitionId}`,
                            pageSize: 5,
                            state: participantState,
                        },
                    },
                );
                if (result.data.success) {
                    commit('setOverviewTopStateSchools', result.data.data);
                    return participantState;
                } else {
                    reportError(
                        'Error reading competition overview top schools',
                        result,
                        () => {
                            commit('setOverviewTopStateSchools', []);
                        },
                    );
                }
            } catch (error) {
                reportError(
                    'Error reading competition overview top schools',
                    error,
                    () => {
                        commit('setOverviewTopStateSchools', []);
                    },
                );
            }
        },
        /** Usage:
         *
           this.$store.dispatch(
            'v2/competition/fetchCurrentCompetitionLeaderboard',
            {
                competitionId: 'math-madness-25',
                grade: '1',
            },
        );
         */
        fetchCurrentCompetitionLeaderboard: ({ state, dispatch }, payload) => {
            const competitionId =
                payload.competitionId || state.selectedCompetition?.id;

            if (!competitionId) {
                console.error(
                    'fetchCurrentCompetitionLeaderboard:: Missing competitionId',
                );
                return;
            }

            const params = {
                ...payload,
                leaderboardId: competitionId,
            };

            return dispatch('fetchLeaderboard', params);
        },
        /** Usage:
         *
           this.$store.dispatch(
            'v2/competition/fetchMyClassLeaderboard',
            {
                competitionId: 'math-madness-25',
                participantId: 11810809,
                grade: '1',
            },
        );
         */
        fetchMyClassLeaderboard: ({ state, dispatch }, payload) => {
            const competitionId =
                payload.competitionId || state.selectedCompetition?.id;

            if (!competitionId) {
                console.error(
                    'fetchCurrentCompetitionLeaderboard:: Missing competitionId',
                );
                return;
            }

            const params = {
                ...payload,
                leaderboardId: competitionId,
            };

            return dispatch('fetchLeaderboard', params);
        },
        /** Usage:
         *
            this.$store.dispatch('v2/competition/fetchSchoolLeaderboard', {
                competitionId: 'math-madness-25',
            });
         */
        fetchSchoolLeaderboard: ({ state, dispatch }, payload) => {
            const competitionId =
                payload.competitionId || state.selectedCompetition?.id;

            if (!competitionId) {
                console.error('fetchSchoolLeaderboard:: Missing competitionId');
                return;
            }

            const params = {
                ...payload,
                leaderboardId: `schools_${competitionId}`,
            };

            return dispatch('fetchLeaderboard', params);
        },
        fetchMastersLeagueLeaderboard: async (
            { state, dispatch, commit, getters },
            payload,
        ) => {
            let { seasonId } = payload;
            // @TODO: make this dynamic from BE server
            seasonId = 'masters-spring-25';
            if (!seasonId) {
                console.error(
                    'fetchMastersLeagueLeaderboard:: Missing seasonId',
                );
                return;
            }
            if (!payload.grade) {
                console.warn('fetchMastersLeagueLeaderboard:: Missing grade');
                return;
            }
            const params = {
                ...payload,
                leaderboardId: seasonId,
            };

            const result = await dispatch('fetchLeaderboard', params);
            if (payload.own) {
                commit(
                    'setSeasonTotalParticipants',
                    result[0]?.totalParticipants || 0,
                );
                if (payload.participantId) {
                    if (result.length > 0) {
                        await dispatch(
                            'calculateMySeasonLeague',
                            payload.participantId,
                        );

                        if (
                            !result.some(
                                (record) =>
                                    record.classCode === payload.participantId,
                            )
                        ) {
                            commit('setOwnRecords', []);
                            commit(
                                'setCustomGrade',
                                getters.getCompetitionGrade,
                            );
                        }
                    }
                }
            }
        },

        fetchLeaderboard: async ({ state, getters, commit }, payload) => {
            try {
                const result = await SecureApi().get(
                    'competitions/leaderboard',
                    {
                        params: {
                            ..._.pick(payload, [
                                'leaderboardId',
                                'participantId',
                                'grade',
                                'school',
                                'state',
                                'lastPosition',
                                'moveUp',
                            ]),
                            pageSize: payload.pageSize ?? 10,
                        },
                    },
                );
                if (result.data.success) {
                    commit('setLeaderboard', result.data.data);
                    const newChunk = result.data.data;
                    let board;

                    if (!payload.lastPosition || payload.reset) {
                        commit(
                            'setLeaderboardUpdateTime',
                            newChunk[0]?.lastComputedAt,
                        );
                        const datasetSize =
                            payload.forcedTotal ??
                            newChunk[0]?.filteredParticipants ??
                            newChunk[0]?.totalParticipants;
                        // size restriction is for the case like top 500
                        let total = payload.restrictSize
                            ? Math.min(payload.pageSize, datasetSize)
                            : datasetSize;
                        // avoid mismatch output
                        if (
                            datasetSize > newChunk.length &&
                            payload.pageSize > newChunk.length
                        ) {
                            total = newChunk.length;
                        }
                        board = Array(total).fill({});
                        newChunk.forEach((item, index) => {
                            board[index] = item;
                        });
                    } else {
                        board = [...state.infiniteLeaderboard];
                        const indexOfLastStored = board.findIndex(
                            (item) => item.position === payload.lastPosition,
                        );
                        if (indexOfLastStored === -1) {
                            console.warn(
                                'fetchLeaderboard: lastPosition not found; no place to add new chunk',
                            );
                        } else {
                            // append chunk
                            newChunk.forEach((item, index) => {
                                const updatePos = indexOfLastStored + 1 + index;
                                if (updatePos >= board.length) {
                                    console.warn(
                                        'new chunk overflows the leaderboard; skipping',
                                    );
                                    return;
                                }
                                board[updatePos] = item;
                            });
                            // crop array if chunk is not full
                            if (
                                newChunk.length &&
                                newChunk.length < payload.pageSize
                            ) {
                                board.length =
                                    indexOfLastStored + 1 + newChunk.length;
                                console.warn(
                                    'chunk is not full; cropping array to',
                                    board.length,
                                );
                            }
                        }
                    }
                    if (payload.own) {
                        commit('setOwnRecords', result.data.data);
                    } else {
                        commit('setInfiniteLeaderboard', board);
                    }
                    return result.data.data;
                } else {
                    reportError(
                        'Error reading competition leaderboard',
                        result,
                        () => {
                            commit('setLeaderboard', []);
                        },
                    );
                }
            } catch (error) {
                reportError(
                    'Error reading competition leaderboard',
                    error,
                    () => {
                        commit('setLeaderboard', []);
                    },
                );
            }
        },

        fetchNeighbors: async ({ state, getters, commit }, payload = {}) => {
            const comp = getters.currentCompetitionEvent;
            const competitionId =
                payload.competitionId ??
                (comp.status === 'Current' ? comp.event.id : null);
            if (!competitionId) {
                console.error('fetchNeighbors:: Missing competitionId');
                return;
            }

            const params = {
                ...payload,
                leaderboardId: competitionId,
                participantId:
                    payload.classCode ?? state.competitionTeam?.classCode,
                grade: payload.grade ?? state.competitionTeam?.grade,
                pageSize: payload.pageSize ?? 104,
                tf: true,
            };
            try {
                const result = await SecureApi().get(
                    'competitions/leaderboard',
                    { params },
                );
                if (result.data.success) {
                    commit('setNeighbors', result.data.data);
                    return result.data.data;
                }
                return null;
            } catch (error) {
                return null;
            }
        },
        async fetchCurrentYearlyLeague({ state, dispatch }) {
            const ownParams = {
                own: true,
                pageSize: 1,
                participantId: state.competitionTeam?.classCode,
                grade: state.competitionTeam?.grade,
            };

            await dispatch('fetchMastersLeagueLeaderboard', ownParams);
        },

        calculateMySeasonLeague: async (
            { state, commit, dispatch },
            ownClassCode,
        ) => {
            const ownPosition = state.ownRecords.find(
                (record) => record.classCode === ownClassCode,
            )?.position;
            const myLeague = await dispatch(
                'calculateSeasonLeague',
                ownPosition,
            );
            if (ownPosition && myLeague) {
                commit('setSelectedYearlyLeague', myLeague);
            } else {
                commit(
                    'setSelectedYearlyLeague',
                    LEAGUES_CONFIG[LEAGUES_CONFIG.length - 1],
                );
                console.warn('No league found for the given position');
            }
        },
        calculateSeasonLeague: ({ state }, position) => {
            const totalParticipants = state.seasonTotalParticipants;
            if (
                !position ||
                !totalParticipants ||
                position > totalParticipants
            ) {
                console.error('Invalid position or total participants');
                return;
            }

            const percentile = (1 - (position - 1) / totalParticipants) * 100;

            const league = LEAGUES_CONFIG.find(
                (l) => percentile >= l.from && percentile <= l.to,
            );
            return league;
        },

        registerTeam: async ({ state, commit }, { classCode, formData }) => {
            let result, errorMessage;
            try {
                result = await SecureApi().post('competitions/register', {
                    classCode: classCode,
                    grade: formData.grade,
                    state: formData.state,
                    school: capitalize(formData.schoolName),
                    publicName: formData.publicName,
                });
            } catch (error) {
                errorMessage = error?.message;
                return {
                    success: false,
                    errorMessage,
                };
            }

            const { success, error, data: response } = result.data;
            if (success) {
                new TrackingService().track(
                    EVENTS.MONTHLY_COMPETITION_REGISTERS_CLASS,
                    {
                        classCode: classCode,
                        grade: formData.grade,
                        state: formData.state,
                        schoolName: capitalize(formData.schoolName),
                        publicClassName: formData.publicName,
                    },
                );
                commit('addOrUpdateTeam', response);
            } else {
                errorMessage = error?.message?.message;
            }

            return {
                success,
                errorMessage,
            };
        },
        editTeam: async ({ state, commit }, { classCode, formData }) => {
            let result, errorMessage;
            try {
                result = await SecureApi().patch('competitions/edit', {
                    classCode: classCode,
                    school: capitalize(formData.schoolName),
                    publicName: formData.publicName,
                });
            } catch (error) {
                errorMessage = error?.message;
                return {
                    success: false,
                    errorMessage,
                };
            }

            const { success, error, data: response } = result.data;
            if (success) {
                new TrackingService().track(
                    EVENTS.MONTHLY_COMPETITION_EDITS_CLASS,
                    {
                        classCode: classCode,
                        grade: formData.grade,
                        state: formData.state,
                        schoolName: capitalize(formData.schoolName),
                        publicClassName: formData.publicName,
                    },
                );
                commit('addOrUpdateTeam', response);
            } else {
                errorMessage = error?.message?.message;
            }

            return {
                success,
                errorMessage,
            };
        },

        fetchLeaderboardTopStudents: async ({ state, commit }) => {
            const competitionId = state.selectedCompetition?.id;

            try {
                const result = await SecureApi().get(
                    'competitions/leaderboard',
                    {
                        params: {
                            leaderboardId: `studentsTop_${competitionId}`,
                            pageSize: 6,
                        },
                    },
                );

                if (result.data.success) {
                    commit('setLeaderboardTopStudents', result.data.data);
                } else {
                    reportError(
                        'Error reading competition leaderboard top students',
                        result,
                        () => {
                            commit('setLeaderboardTopStudents', []);
                        },
                    );
                }
            } catch (error) {
                reportError(
                    'Error reading competition leaderboard top students',
                    error,
                    () => {
                        commit('setLeaderboardTopStudents', []);
                    },
                );
            }
        },
        fetchLeaderboardMostImproved: async ({ state, commit }) => {
            const competitionId = state.selectedCompetition?.id;

            try {
                const result = await SecureApi().get(
                    'competitions/leaderboard',
                    {
                        params: {
                            leaderboardId: `studentsRaise_${competitionId}`,
                            pageSize: 6,
                        },
                    },
                );

                if (result.data.success) {
                    commit('setLeaderboardMostImproved', result.data.data);
                } else {
                    reportError(
                        'Error reading competition leaderboard most improved students',
                        result,
                        () => {
                            commit('setLeaderboardMostImproved', []);
                        },
                    );
                }
            } catch (error) {
                reportError(
                    'Error reading competition leaderboard most improved students',
                    error,
                    () => {
                        commit('setLeaderboardMostImproved', []);
                    },
                );
            }
        },
        fetchLeaderboardTopHomePlayers: async ({ state, commit }) => {
            const competitionId = state.selectedCompetition?.id;

            try {
                const result = await SecureApi().get(
                    'competitions/leaderboard',
                    {
                        params: {
                            leaderboardId: `studentsHome_${competitionId}`,
                            pageSize: 6,
                        },
                    },
                );

                if (result.data.success) {
                    commit('setLeaderboardTopHomePlayers', result.data.data);
                } else {
                    reportError(
                        'Error reading competition leaderboard most improved students',
                        result,
                        () => {
                            commit('setLeaderboardTopHomePlayers', []);
                        },
                    );
                }
            } catch (error) {
                reportError(
                    'Error reading competition leaderboard most improved students',
                    error,
                    () => {
                        commit('setLeaderboardTopHomePlayers', []);
                    },
                );
            }
        },

        fetchPinnedClasses: async ({ state, commit }) => {
            const dropPinnedClasses = () => {
                commit('setPinnedClasses', []);
            };

            try {
                const result = await SecureApi().get(
                    'competitions/participant/pinned',
                );

                if (result.data.success) {
                    commit('setPinnedClasses', result.data.data);
                } else {
                    reportError(
                        'Unable to pin class',
                        result,
                        dropPinnedClasses,
                    );
                }
            } catch (error) {
                reportError('Unable to pin class', error, dropPinnedClasses);
            }
        },

        pinClass: async ({ state, getters, commit }, classCode) => {
            if (!classCode || getters.pinnedClassCodes.includes(classCode)) {
                console.warn('Invalid classCode or already pinned');
                return;
            }

            try {
                const result = await SecureApi().post(
                    'competitions/participant/pin',
                    { classCode },
                );

                if (result.data.success) {
                    commit('addPinnedClass', result.data.data);
                    new TrackingService().track(
                        EVENTS.MONTHLY_COMPETITION_TEACHER_PINS_CLASS,
                    );
                } else {
                    reportError('Unable to pin class', result);
                }
            } catch (error) {
                reportError('Unable to pin class', error);
            }
        },

        unpinClass: async ({ state, commit }, classCode) => {
            if (!classCode) return;

            try {
                const result = await SecureApi().post(
                    'competitions/participant/unpin',
                    { classCode },
                );

                if (result.data.success) {
                    commit('removePinnedClass', classCode);
                } else {
                    reportError('Unable to unpin class', result);
                }
            } catch (error) {
                reportError('Unable to unpin class', error);
            }
        },

        markRewardAsSeen: async (_, payload) => {
            try {
                const competitionId = payload?.competitionId;
                if (!competitionId) return;

                const result = await SecureApi().post(
                    `competitions/rewards/${competitionId}/seen`,
                    {
                        competitionId,
                        classCodes: payload.classCodes || [],
                    },
                );

                if (!result.data.success) {
                    reportError('Unable to mark reward as seen', result);
                }
            } catch (error) {
                reportError('Unable to mark reward as seen', error);
            }
        },

        fetchSuggestionLeaderboard: async ({ state, getters, commit }) => {
            commit('setSuggestionLeaderboard', []);
            const leaderboardId = state.selectedCompetition?.id;
            const teams = state.teams;
            const teamCodes = teams.map((team) => team.classCode);
            const pinnedCodes = state.pinnedClasses.map(
                (team) => team.classCode,
            );
            const schools = getters.competitionSchools;

            const otherTeams = [];
            function addSuggestion(acc, suggestion, school, role) {
                if (teamCodes.includes(suggestion.classCode)) return;
                if (pinnedCodes.includes(suggestion.classCode)) return;
                acc.push({
                    ...suggestion,
                    reason: `${role} class of the ${suggestion.grade}${getGradeEnding(suggestion.grade)} grade in ${school}`,
                });
            }

            try {
                commit('setSuggestionsLoading', true);
                commit(
                    'setSuggestionsOptionsAvailable',
                    schools.length * 8 + teams.length,
                );
                commit('setSuggestionsOptionsLoaded', 0);

                for (let i = 0; i < schools.length; i++) {
                    const school = schools[i];
                    const compareByGradeInclusion = (a, b) => {
                        const aInGrades = school.grades.includes(a.grade);
                        const bInGrades = school.grades.includes(b.grade);
                        return bInGrades - aInGrades;
                    };
                    commit(
                        'setSuggestionsOptionsLoaded',
                        state.suggestionsOptionsLoaded + 8,
                    );
                    const result = await SecureApi().get(
                        'competitions/participant/others',
                        {
                            params: {
                                school: school.school,
                                state: school.state,
                                pageSize: 25,
                            },
                        },
                    );

                    if (result.data.success) {
                        let apiPeers = [...result.data.data];
                        if (!apiPeers.length) continue;

                        // workaround BE cutting selves
                        // temporarily add them back to define a self-leader
                        const myTeamsInSchool = teams.filter(
                            (t) =>
                                t.state === school.state &&
                                t.school === school.school,
                        );
                        apiPeers.push(...myTeamsInSchool);

                        apiPeers = _.orderBy(
                            apiPeers,
                            ['totalScore'],
                            ['desc'],
                        );

                        const thisSchoolPeers = [];
                        const gradesWithLeader = new Set();

                        apiPeers.forEach((peer) => {
                            if (!gradesWithLeader.has(peer.grade)) {
                                gradesWithLeader.add(peer.grade);
                                addSuggestion(
                                    thisSchoolPeers,
                                    peer,
                                    school.school,
                                    'Leader',
                                );
                            } else {
                                addSuggestion(
                                    thisSchoolPeers,
                                    peer,
                                    school.school,
                                    'Peer',
                                );
                            }
                        });

                        // bring sibling grades to the top
                        thisSchoolPeers.sort(compareByGradeInclusion);

                        otherTeams.push(...thisSchoolPeers.slice(0, 5));
                    } else {
                        reportError(
                            `Unable to fetch suggestions for ${school.school}, ${school.state}`,
                            result,
                        );
                    }
                }

                const neighbors = [];
                for (let i = 0; i < teams.length; i++) {
                    commit(
                        'setSuggestionsOptionsLoaded',
                        state.suggestionsOptionsLoaded + 1,
                    );
                    if (!teams[i].rank) continue;
                    const result = await SecureApi().get(
                        'competitions/leaderboard',
                        {
                            params: {
                                leaderboardId,
                                pageSize: 11,
                                grade: String(teams[i].grade),
                                participantId: teams[i].classCode,
                            },
                        },
                    );
                    if (result.data.success) {
                        const peers = _.sampleSize(
                            [...result.data.data].filter(
                                (team) =>
                                    !teamCodes.includes(team.classCode) &&
                                    !pinnedCodes.includes(team.classCode),
                            ),
                            5,
                        ).map((peer) => ({
                            ...peer,
                            reason: `Leaderboard neighbor of ${teams[i].publicName}`,
                        }));
                        neighbors.push(...peers);
                    } else {
                        reportError(
                            `Unable to fetch neighbor suggestions for ${teams[i].publicName}`,
                            result,
                        );
                    }
                }
                const fewNeighbors = _.sampleSize(
                    _.uniqBy(neighbors, 'classCode'),
                    5,
                );
                // console.debug('neighbors', neighbors, fewNeighbors);
                otherTeams.push(...fewNeighbors);
                commit(
                    'setSuggestionLeaderboard',
                    _.uniqBy(otherTeams, 'classCode'),
                );
            } catch (error) {
                reportError('Unable to fetch suggestions', error, () => {
                    commit('setSuggestionLeaderboard', otherTeams);
                });
            } finally {
                commit('setSuggestionsLoading', false);
                commit('setSuggestionsOptionsAvailable', 0);
                commit('setSuggestionsOptionsLoaded', 0);
            }
        },
    },
};
